import { Spin } from 'antd'
import classNames from 'classnames'
import React, { FC, Suspense, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { generatePath } from '~/app/lib/function'
import { AUTH_PATH, QACHAT_PATH } from '~/app/lib/paths'
import { useAuthStore } from '~/app/services/auth'
import { useGlobalState } from '~/app/services/global'
import { useGetMe, useUserStore } from '~/app/services/user'
import {
  useDefaultWorkspaceStore,
  useSelectedWorkspaceStore,
} from '~/app/services/workspace'
import VersionDisplay from '../../utils/VersionDisplay'
import styles from './AuthLayout.module.scss'

interface AuthLayoutProps {
  children: React.ReactNode
}

const AuthLayout: FC<AuthLayoutProps> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const currentUser = useUserStore((state) => state.currentUser)
  const token = useAuthStore((state) => state.accessToken)
  const defaultWorkspace = useDefaultWorkspaceStore((state) => state.defaultWorkspace)
  const logout = useUserStore((state) => state.logout)
  const clearAuth = useAuthStore((state) => state.clearAuth)
  const setSelectedWorkspace = useSelectedWorkspaceStore((state) => state.setSelectedWorkspace)
  const setDefaultWorkspace = useDefaultWorkspaceStore((state) => state.setDefaultWorkspace)
  const { mutate: getMe, isError } = useGetMe()

  const { appLoading, setAppLoading } = useGlobalState()

  useEffect(() => {
    if (location.pathname === AUTH_PATH.FORGOT_PASSWORD) {
      logout()
      clearAuth()
      setSelectedWorkspace(null)
      setDefaultWorkspace(null)
    }
  }, [location.pathname, logout, clearAuth, setSelectedWorkspace, setDefaultWorkspace])

  useEffect(() => {
    if (
      !!token &&
      location.pathname !== AUTH_PATH.FORGOT_PASSWORD &&
      location.pathname !== AUTH_PATH.TENANT_SELECTION
    ) {
      getMe()
    }
  }, [token, location.pathname, getMe])

  useEffect(() => {
    setAppLoading?.(true)

    // Don't auto-redirect if user is on workspace selection page
    if (location.pathname === AUTH_PATH.SELECT_WORKSPACE) {
      setAppLoading?.(false)
      return
    }

    if (currentUser?.id && defaultWorkspace?.id) {
      navigate(generatePath(QACHAT_PATH.DEFAULT, defaultWorkspace?.id), { replace: true })
      return
    }

    setAppLoading?.(false)
  }, [navigate, setAppLoading, currentUser?.id, defaultWorkspace?.id, location.pathname])

  useEffect(() => {
    if (!isError) return

    setAppLoading?.(false)
  }, [isError, setAppLoading])

  return (
    <div className={styles.auth_layout}>
      {appLoading ? (
        <div className="flex h-screen w-screen items-center justify-center">
          <Spin />
        </div>
      ) : (
        <Suspense>
          {location.pathname === AUTH_PATH.SELECT_WORKSPACE ||
          location.pathname === AUTH_PATH.TENANT_SELECTION ? null : (
            <div className={classNames(styles.left, 'flex-1')}>
              <div className="flex flex-col gap-10">
                <div className="relative z-0">
                  <img
                    alt="logo"
                    src="/auth/qagenie_highlight_logo.svg"
                    width={212}
                    height={190}
                    className="z-10"
                  />

                  <div className={styles.highlight_logo_blur}>
                    <img alt="logo" src="/auth/qagenie_highlight_logo.svg" />
                  </div>
                </div>

                <img alt="Text Logo" src="/auth/qagenie_text_logo.svg" width={212} height={39} />
              </div>
            </div>
          )}
          <div className={classNames(styles.right, 'flex-1')}>
            <img
              className={styles.mobile_logo}
              src="/svgs/qagenie_logo_full.svg"
              alt="Logo"
              width={145}
              height={36}
            />

            <div className={classNames('w-full pt-20 md:max-w-[390px] md:pt-0')}>{children}</div>
          </div>
        </Suspense>
      )}
      <VersionDisplay />
    </div>
  )
}

export default AuthLayout
